import React, { useState, useEffect } from 'react';
import { Button, Card, Badge } from 'react-bootstrap';
import ExerciseLibrary from './components/ExerciseLibrary';
import WorkoutLogger from './components/WorkoutLogger';
import TemplateManager from './components/TemplateManager';
import { useWorkoutDraft } from './hooks/useWorkoutDraft';
import './styles/WorkoutTracker.scss';

function WorkoutTracker() {
  const [view, setView] = useState('landing');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const { hasDraft, draftAge, checkDraft } = useWorkoutDraft();

  // Check for draft when landing page loads
  useEffect(() => {
    checkDraft();
  }, [checkDraft]);

  console.log('🏠 Landing page - Draft status:', { hasDraft, draftAge });

  const renderLandingPage = () => {
    return (
      <div className="workout-tracker-landing">
        <div className="text-center mb-5">
          <h1 className="display-4">
            <i className="fas fa-dumbbell me-3"></i>
            Workout Tracker
          </h1>
          <p className="lead text-muted">Track your workouts, build programs, and monitor your progress</p>
        </div>

        <div className="row g-4">
          {/* Start New Workout */}
          <div className="col-md-6">
            <Card className="h-100 workout-card">
              <Card.Body className="d-flex flex-column">
                <div className="text-center mb-3">
                  <i className="fas fa-play-circle fa-4x text-success"></i>
                  {hasDraft && (
                    <Badge bg="warning" className="mt-2 d-block">
                      <i className="fas fa-save me-1"></i>
                      Draft Saved
                    </Badge>
                  )}
                </div>
                <Card.Title className="text-center mb-3">Start New Workout</Card.Title>
                <Card.Text className="text-center text-muted flex-grow-1">
                  {hasDraft
                    ? `Resume your workout from ${draftAge}`
                    : 'Begin a new workout session and log your exercises'
                  }
                </Card.Text>
                <Button
                  variant="success"
                  size="lg"
                  className="mt-3"
                  onClick={() => setView('logger')}
                >
                  <i className={`fas ${hasDraft ? 'fa-redo' : 'fa-play'} me-2`}></i>
                  {hasDraft ? 'Resume Workout' : 'Start Workout'}
                </Button>
              </Card.Body>
            </Card>
          </div>

          {/* Start from Template */}
          <div className="col-md-6">
            <Card className="h-100 workout-card">
              <Card.Body className="d-flex flex-column">
                <div className="text-center mb-3">
                  <i className="fas fa-file-alt fa-4x text-primary"></i>
                </div>
                <Card.Title className="text-center mb-3">Start from Template</Card.Title>
                <Card.Text className="text-center text-muted flex-grow-1">
                  Choose a workout template and begin your session
                </Card.Text>
                <Button
                  variant="primary"
                  size="lg"
                  className="mt-3"
                  onClick={() => setView('templates')}
                >
                  <i className="fas fa-copy me-2"></i>
                  View Templates
                </Button>
              </Card.Body>
            </Card>
          </div>

          {/* Manage Exercises */}
          <div className="col-md-6">
            <Card className="h-100 workout-card">
              <Card.Body className="d-flex flex-column">
                <div className="text-center mb-3">
                  <i className="fas fa-list fa-4x text-info"></i>
                </div>
                <Card.Title className="text-center mb-3">Manage Exercises</Card.Title>
                <Card.Text className="text-center text-muted flex-grow-1">
                  Create and organize your exercise library
                </Card.Text>
                <Button
                  variant="info"
                  size="lg"
                  className="mt-3"
                  onClick={() => setView('exercises')}
                >
                  <i className="fas fa-dumbbell me-2"></i>
                  Exercise Library
                </Button>
              </Card.Body>
            </Card>
          </div>

          {/* View Analytics */}
          <div className="col-md-6">
            <Card className="h-100 workout-card">
              <Card.Body className="d-flex flex-column">
                <div className="text-center mb-3">
                  <i className="fas fa-chart-line fa-4x text-warning"></i>
                </div>
                <Card.Title className="text-center mb-3">View Analytics</Card.Title>
                <Card.Text className="text-center text-muted flex-grow-1">
                  Track progress, view PRs, and analyze performance
                </Card.Text>
                <Button
                  variant="warning"
                  size="lg"
                  className="mt-3"
                  disabled
                >
                  <i className="fas fa-chart-bar me-2"></i>
                  Analytics
                </Button>
                <small className="text-muted text-center mt-2">Coming in Phase 6</small>
              </Card.Body>
            </Card>
          </div>
        </div>

        {/* Quick Stats - Placeholder for future */}
        <div className="row mt-5">
          <div className="col-12">
            <Card>
              <Card.Body>
                <h5 className="mb-3">
                  <i className="fas fa-history me-2"></i>
                  Recent Activity
                </h5>
                <p className="text-muted text-center py-4">
                  Your workout history will appear here once you start logging workouts
                </p>
              </Card.Body>
            </Card>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="workout-tracker-container">
      {view === 'landing' && renderLandingPage()}
      {view === 'exercises' && <ExerciseLibrary onBack={() => setView('landing')} />}
      {view === 'templates' && (
        <TemplateManager
          onBack={() => setView('landing')}
          onStartWorkout={(template) => {
            setSelectedTemplate(template);
            setView('logger');
          }}
        />
      )}
      {view === 'logger' && (
        <WorkoutLogger
          template={selectedTemplate}
          onFinish={() => {
            setSelectedTemplate(null);
            setView('landing');
          }}
          onCancel={() => {
            setSelectedTemplate(null);
            setView('landing');
          }}
        />
      )}
    </div>
  );
}

export default WorkoutTracker;
